/* eslint-disable no-empty */
/* eslint-disable no-prototype-builtins */
import { Collapse, FormControlLabel, Grid, Stack } from '@mui/material';
import _ from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { useExpenseApi } from '../../hooks/useExpenseApi';
import {
  ConveyanceVehicleType,
  ExpenseClaim,
  ExpenseDocument,
  ExpenseType,
  ExpenseVisitData,
} from '../../types/claim';
import { DateUtility } from '../../utils/DateUtlility';
import { StringUtility } from '../../utils/StringUtility';
import LmButton from '../common/LmButton';
import LmDateField from '../common/LmDateField';
import LmTextField from '../common/LmTextField';
import VegaCheckbox from '../common/VegaCheckbox';
import VegaDrawerContent from '../common/VegaDrawerContent';
import VegaFileSelect, {
  AcceptedExtension,
  VegaFileSelectOption,
} from '../common/VegaFileSelect';
import VegaFormInputField from '../common/VegaFormInputField';
import VegaSelect, { VegaSelectOption } from '../common/VegaSelect';
import VegaText from '../common/VegaText';

type Props = {
  onUpdateClick: (claim: ExpenseClaim, formData: UpdateClaimFormData) => void;
  value?: ExpenseClaim;
};

enum FormField {
  Date = 'date',
  ExpenseType = 'expenseType',
  Amount = 'amount',
  Description = 'description',
  Documents = 'documents',
  VehicleType = 'vehicleType',
}

export type CreateClaimFormData = {
  date: Date;
  expenseType: string;
  amount: number;
  description: string;
  documents: VegaFileSelectOption[];
  vehicleType: string;
};

export type UpdateClaimFormData = {
  amount: number;
  description: string;
  documentsToBeUploaded: VegaFileSelectOption[];
  documentsToBeDeleted: VegaFileSelectOption[];
};

const yesterDayDate = DateUtility.subtractDays(new Date(), 1);
function UpdateClaimForm({ value, onUpdateClick }: Props) {
  const { fetchExpenseClaim } = useExpenseApi();
  const [formData, setFormData] = useState<Partial<CreateClaimFormData>>({
    date: yesterDayDate,
    documents: [],
  });
  const [showDescriptionInput, setShowDescriptionInput] =
    useState<boolean>(false);
  const [showVehicleInput, setShowVehicleInput] = useState<boolean>(false);
  const [showAmountInput, setShowAmountInput] = useState<boolean>(false);
  const [showSystemGeneratedAmount, setShowSystemGeneratedAmount] =
    useState<boolean>(false);
  const [showDeviationAmountInput, setShowDeviationAmountInput] =
    useState<boolean>(false);
  const [visitData, setVisitData] = useState<Partial<ExpenseVisitData>>({});
  const [documentsToBeDeleted, setDocumentsToBeDeleted] = useState<
    VegaFileSelectOption[]
  >([]);
  const [claim, setClaim] = useState<ExpenseClaim | null>(null);

  function updateFormData(field: FormField, value: any) {
    setFormData(prev => {
      return {
        ...prev,
        [field]: value,
      };
    });
  }

  function onAmountChange(value: string) {
    const parsedNumber = parseFloat(value);
    const isValidNumber = isNaN(parsedNumber) == false;
    updateFormData(FormField.Amount, isValidNumber ? parsedNumber : undefined);
  }

  function onDescriptionChange(value: string) {
    updateFormData(FormField.Description, value);
  }

  function onFileChange(index: number, file: VegaFileSelectOption) {
    const existingDocuments = formData.documents ?? [];
    if (index >= 0 && index < existingDocuments.length) {
      existingDocuments[index] = file;
    } else {
      existingDocuments.push(file);
    }
    updateFormData(FormField.Documents, existingDocuments);
  }

  function onFileAdd(file: VegaFileSelectOption) {
    const size = (formData.documents ?? []).length;
    onFileChange(size, file);
  }

  function onFileDelete(index: number, file: VegaFileSelectOption) {
    const existingDocuments = formData.documents ?? [];
    if (existingDocuments.length <= 0) return;
    if (index >= 0 && index < existingDocuments.length) {
      existingDocuments.splice(index, 1);
    }
    updateFormData(FormField.Documents, existingDocuments);
    setDocumentsToBeDeleted(prev => [...prev, file]);
  }

  const isInputValid = () => {
    const isDateValid = formData.date != null;
    const isTypeOfExpenseValid = formData.expenseType != null;
    let isVehicleValid = true;
    let isSystemGeneratedAmountValid = true;
    let isKmsTravelledValid = true;
    let isAmountValid = true;
    let isDescriptionValid = true;
    const isDocumentValid =
      formData.documents != null && formData.documents.length > 0;

    if (formData.expenseType == ExpenseType.CONVEYANCE) {
      isVehicleValid = formData.vehicleType != null;
      isSystemGeneratedAmountValid = visitData.projectedPrice != null;
      isKmsTravelledValid = visitData.distance != null;
      if (showDeviationAmountInput == true) {
        isAmountValid = formData.amount != null && formData.amount > 0;
        isDescriptionValid = formData.description != null;
      }
    } else if (formData.expenseType == ExpenseType.OTHERS) {
      isDescriptionValid = formData.description != null;
    } else {
      isAmountValid = formData.amount != null && formData.amount > 0;
    }

    return (
      isDateValid &&
      isTypeOfExpenseValid &&
      isAmountValid &&
      isKmsTravelledValid &&
      isVehicleValid &&
      isSystemGeneratedAmountValid &&
      isDescriptionValid &&
      isDocumentValid
    );
  };

  async function fetchClaimById(claimId: string) {
    try {
      const response = await fetchExpenseClaim({
        id: claimId,
        includeDocumentMetaData: true,
      });
      setClaim(response);
      const parsedDate = DateUtility.parseStringToDate(response?.dateOfExpense);
      const expenseType = response?.expenseType;
      const metaData = response?.metaData;
      let supportingDocuments: ExpenseDocument[] = [];
      if (metaData.hasOwnProperty('supportingDocuments')) {
        supportingDocuments = metaData[
          'supportingDocuments'
        ] as ExpenseDocument[];
      }

      const expenseDocuments = supportingDocuments.map(item => {
        const option: VegaFileSelectOption = {
          id: item.id,
          label: item.name,
        };
        return option;
      });

      setFormData({
        date: parsedDate,
        expenseType: expenseType,
        amount: response?.amount,
        description: response?.description,
        documents: expenseDocuments,
        vehicleType: response?.conveyanceVehicleType,
      });

      setVisitData({
        distance: response?.distance,
        ratePerKm: response?.ratePerKm,
        projectedPrice: response?.projectedPrice,
      });
      setShowVehicleInput(expenseType == ExpenseType.CONVEYANCE);
      setShowDescriptionInput(expenseType == ExpenseType.OTHERS);
      setShowSystemGeneratedAmount(expenseType == ExpenseType.CONVEYANCE);
      setShowAmountInput(
        expenseType == ExpenseType.OTHERS || expenseType == ExpenseType.MEALS
      );
      if (expenseType == ExpenseType.CONVEYANCE) {
        setShowDeviationAmountInput(response.description != null);
      }
    } catch (error) {}
  }

  useEffect(() => {
    const claimId = value?.id;
    if (claimId) {
      fetchClaimById(claimId);
    }
  }, [value]);

  async function onSaveClick() {
    if (claim == null) return;
    const documents = formData.documents ?? [];
    const filesToBeUploaded: VegaFileSelectOption[] = documents.filter(
      item => item.value != null || item.value != undefined
    );
    const updatedFormData: UpdateClaimFormData = {
      amount: formData.amount!,
      description: formData.description!,
      documentsToBeUploaded: filesToBeUploaded,
      documentsToBeDeleted: documentsToBeDeleted,
    };
    onUpdateClick(claim, updatedFormData);
  }

  return (
    <VegaDrawerContent
      renderBottomView={() => {
        return (
          <LmButton
            text="Save Details"
            disabled={isInputValid() == false}
            onClick={onSaveClick}
          />
        );
      }}
    >
      <Grid container>
        <Grid item xs={12}>
          <LmDateField
            header="Date"
            onChange={() => {}}
            value={formData.date}
            maxDate={yesterDayDate}
            disabled
          />
        </Grid>

        <Grid item xs={12} style={{ marginTop: '1.25rem' }}>
          <VegaFormInputField label="Type Of Expense">
            <VegaSelect
              options={expenseOptions()}
              value={formData.expenseType ?? ''}
              disabled
            />
          </VegaFormInputField>
        </Grid>

        <AnimatedInputWrapper show={showAmountInput} marginTop={'1.25rem'}>
          <LmTextField
            placeholder="Amount"
            header="Amount"
            value={formData.amount ?? ''}
            onChange={e => onAmountChange(e.target.value)}
          />
        </AnimatedInputWrapper>

        <AnimatedInputWrapper show={showDescriptionInput} marginTop={'1.25rem'}>
          <LmTextField
            placeholder="Enter Description"
            header="Description"
            value={formData.description ?? ''}
            onChange={e => onDescriptionChange(e.target.value)}
          />
        </AnimatedInputWrapper>

        <AnimatedInputWrapper show={showVehicleInput} marginTop={'1.25rem'}>
          <VegaFormInputField label="Type Of Vehicle">
            <VegaSelect
              options={vehicleOptions()}
              value={formData.vehicleType ?? ''}
              disabled
            />
          </VegaFormInputField>
        </AnimatedInputWrapper>

        <AnimatedInputWrapper
          show={showSystemGeneratedAmount}
          marginTop={'1.25rem'}
        >
          <LmTextField
            header="Kms Travelled"
            value={`${visitData.distance ?? '-'} KMS`}
            disabled
          />
        </AnimatedInputWrapper>

        <Grid
          item
          xs={12}
          style={{ marginTop: showSystemGeneratedAmount ? '1.25rem' : '0rem' }}
        >
          <Collapse in={showSystemGeneratedAmount} timeout={300}>
            <Stack>
              <LmTextField
                placeholder="System Generated Amount"
                header="System Generated Amount"
                value={StringUtility.formatToINR(visitData.projectedPrice)}
                disabled
              />
              <FormControlLabel
                control={
                  <VegaCheckbox
                    checked={showDeviationAmountInput}
                    onChange={(e, checked) => {
                      setShowDeviationAmountInput(checked);
                    }}
                  />
                }
                label={<VegaText text={'Enter your value'} />}
              />
              <Collapse in={showDeviationAmountInput} timeout={300}>
                <LmTextField
                  placeholder="Amount"
                  value={formData.amount}
                  onChange={e => onAmountChange(e.target.value)}
                />
                <div style={{ height: '1.25rem' }}></div>
                <LmTextField
                  placeholder="Enter Reason"
                  header="Reason"
                  value={formData.description ?? ''}
                  onChange={e => onDescriptionChange(e.target.value)}
                />
              </Collapse>
            </Stack>
          </Collapse>
        </Grid>

        <Grid item xs={12} style={{ marginTop: '1.25rem' }}>
          <Stack rowGap={'0.75rem'}>
            {formData.documents?.map((item, index) => {
              return (
                <VegaFileSelect
                  key={index}
                  value={item}
                  acceptedExtension={[
                    AcceptedExtension.JPG,
                    AcceptedExtension.JPEG,
                    AcceptedExtension.PNG,
                    AcceptedExtension.PDF,
                  ]}
                  placeholder={'Upload Document ' + index}
                  onFileSelect={file => {
                    onFileChange(index, file);
                  }}
                  onDeleteClick={() => {
                    onFileDelete(index, item);
                  }}
                />
              );
            })}
            <VegaFileSelect
              placeholder={'Upload Document '}
              onFileSelect={file => {
                onFileAdd(file);
              }}
            />
          </Stack>
        </Grid>
      </Grid>
    </VegaDrawerContent>
  );
}

export default UpdateClaimForm;

type AnimatedInputWrapperProps = {
  show: boolean;
  marginTop: string;
  children: ReactNode;
};

const AnimatedInputWrapper = ({
  show,
  marginTop,
  children,
}: AnimatedInputWrapperProps) => {
  return (
    <Grid item xs={12} style={{ marginTop: show ? marginTop : '0rem' }}>
      <Collapse in={show} timeout={300}>
        {children}
      </Collapse>
    </Grid>
  );
};

const expenseOptions = () =>
  Object.values(ExpenseType).map(item => {
    const option: VegaSelectOption = {
      value: item,
      label: _.startCase(_.toLower(item)),
    };
    return option;
  });

const vehicleOptions = () =>
  Object.values(ConveyanceVehicleType).map(item => {
    const option: VegaSelectOption = {
      value: item,
      label: _.startCase(_.toLower(item)),
    };
    return option;
  });
